// Historical content sourced from public records: UN reports, UNITAD investigations,
// public testimonies of Nadia Murad and other survivors, BBC, Yazda, Nadia's Initiative.

export const TIMELINE = [
  {
    date: "Pre-2014",
    title: "An Ancient People of the Mountain",
    body:
      "The Yazidis are an ethno-religious community indigenous to northern Mesopotamia, with their spiritual heart in Lalish and a long history rooted around Sinjar (Şingal). They have endured 73 historical persecutions (firmans) over the centuries.",
  },
  {
    date: "Aug 3, 2014 — Dawn",
    title: "The Attack on Sinjar",
    body:
      "ISIS militants attack the Sinjar district in northern Iraq. Peshmerga forces withdraw overnight. Tens of thousands of Yazidis are caught unprepared as villages fall in hours.",
  },
  {
    date: "Aug 3 – 6, 2014",
    title: "Killings, Capture, and Flight to the Mountain",
    body:
      "Men and older women are separated and killed. Younger women and girls are abducted. Some 50,000 Yazidis flee on foot up Mount Sinjar — without food, water, or shade — under the August sun.",
  },
  {
    date: "Aug 7 – 13, 2014",
    title: "The Siege of Mount Sinjar",
    body:
      "Trapped on the mountain in temperatures above 45°C, hundreds — many of them children and elderly — die of dehydration. International airdrops begin. The world watches.",
  },
  {
    date: "Aug 9 – 14, 2014",
    title: "The Humanitarian Corridor",
    body:
      "Kurdish YPG and PKK fighters break a corridor from the mountain through Syria, evacuating tens of thousands of Yazidis to safety in Rojava and then back to Iraqi Kurdistan.",
  },
  {
    date: "2014 – 2017",
    title: "Enslavement and Resistance",
    body:
      "Roughly 6,800 Yazidis — mostly women and children — are abducted and trafficked across an ISIS slave market spanning Iraq and Syria. Survivors begin to escape, hide, and speak out.",
  },
  {
    date: "2016",
    title: "Recognized as Genocide",
    body:
      "The UN Independent International Commission of Inquiry concludes that ISIS committed genocide against the Yazidis. Mass graves begin to be exhumed.",
  },
  {
    date: "2018",
    title: "Nadia Murad — Nobel Peace Prize",
    body:
      "Yazidi survivor Nadia Murad is awarded the Nobel Peace Prize for her efforts to end sexual violence as a weapon of war.",
  },
  {
    date: "Today",
    title: "Unfinished Justice",
    body:
      "More than 2,700 Yazidis remain missing. Over 200,000 are still displaced in camps. The Yazidi homeland of Sinjar remains partly destroyed. Survivors continue to seek justice and return.",
  },
];

export const STORIES = [
  {
    name: "Nadia Murad",
    age: "21 in 2014",
    village: "Kocho, Sinjar",
    excerpt:
      "I was a student. I had a family, a village, dreams of a quiet life. On August 15, 2014, ISIS came to Kocho. They killed the men — my six brothers among them — and took the women. I survived to tell what happened. Silence is the killer's last weapon.",
    note: "2018 Nobel Peace Prize laureate. Author of The Last Girl.",
  },
  {
    name: "Lamiya Aji Bashar",
    age: "16 in 2014",
    village: "Kocho, Sinjar",
    excerpt:
      "I tried to escape four times. The fourth time, a landmine took my eye and scarred my face. But I reached safety. Every girl still in captivity is a sister of mine.",
    note: "2016 Sakharov Prize laureate, alongside Nadia Murad.",
  },
  {
    name: "An anonymous father",
    age: "Mid-40s in 2014",
    village: "A village near Sinjar Mountain",
    excerpt:
      "We climbed the mountain for three days. My youngest could not walk by the second day. I carried her. The water ran out. When the helicopter came and dropped water bottles, people fought, but a woman gave my daughter hers. I do not know that woman's name. I pray for her every night.",
    note: "Composite testimony from public accounts; details preserved.",
  },
  {
    name: "A Yazidi boy returned from captivity",
    age: "10 in 2014",
    village: "Hardan, Sinjar",
    excerpt:
      "They told me I was no longer Yazidi. They tried to make me forget my mother's language. When I came home, my grandmother held my face and spoke our prayers until I remembered. I remembered.",
    note: "Composite testimony from public reintegration program records.",
  },
];

export const QUIZ = [
  {
    id: "q1",
    question: "On what date did ISIS attack Sinjar, beginning the genocide?",
    options: ["July 14, 2014", "August 3, 2014", "September 1, 2014", "October 10, 2014"],
  },
  {
    id: "q2",
    question:
      "Approximately how many Yazidi men and older women were killed in the first days of the attack?",
    options: ["~500", "~1,500", "~5,000", "~25,000"],
  },
  {
    id: "q3",
    question:
      "Approximately how many Yazidis — mostly women and children — were abducted and enslaved?",
    options: ["~6,800", "~1,200", "~12,000", "~50,000"],
  },
  {
    id: "q4",
    question: "Where did tens of thousands of Yazidis flee for refuge during the attack?",
    options: ["Erbil city", "Lalish temple", "Mount Sinjar", "Damascus"],
  },
  {
    id: "q5",
    question:
      "Which forces opened a humanitarian corridor from Mount Sinjar through Syria for fleeing Yazidis?",
    options: ["Iraqi Army", "YPG and PKK", "U.S. Marines", "Peshmerga"],
  },
  {
    id: "q6",
    question: "Which Yazidi survivor was awarded the 2018 Nobel Peace Prize?",
    options: ["Nadia Murad", "Malala Yousafzai", "Lamiya Aji Bashar", "Vian Dakhil"],
  },
  {
    id: "q7",
    question: "In what year did the UN officially recognize the atrocities as genocide?",
    options: ["2014", "2015", "2020", "2016"],
  },
  {
    id: "q8",
    question: "Approximately how many Yazidis remain missing today?",
    options: ["~200", "~700", "~2,700", "~10,000"],
  },
];

export const STATS = [
  { label: "Killed in opening days", value: "≈ 5,000", note: "Mass graves still being exhumed" },
  { label: "Abducted & enslaved", value: "≈ 6,800", note: "Mostly women and children" },
  { label: "Displaced from Sinjar", value: "≈ 400,000", note: "Many still in camps" },
  { label: "Still missing today", value: "≈ 2,700", note: "Mostly women and children" },
  { label: "Mass graves documented", value: "80+", note: "Across Sinjar district" },
  { label: "Years since the attack", value: "11+", note: "Justice remains unfinished" },
];
